import React, { useState, useEffect } from "react";
import { Slider } from "@mui/material";

import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import NextButton from "../common/next-button";
import StepNumberBadge from "../common/StepNumberBadge";

type AdvancedSettingsDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNext?: (id: string) => void;
  onAgeChange?: (value: string) => void;
  onGenderChange?: (value: string) => void;
  onAdTextChange?: (value: string) => void;
  onAdHeadlineChange?: (value: string) => void;
  isEditMode?: boolean;
  initialAge?: number | null; // age_min from draft
  initialMaxAge?: number | null; // age_max from draft
  initialGender?: number | null; // 1 for male, 2 for female from draft
  initialAdText?: string; // ad text message from draft
  initialAdHeadline?: string; // ad headline from draft
};

const GENDER_OPTIONS = [
  { id: "all", label: "All" },
  { id: "male", label: "Male" },
  { id: "female", label: "Female" },
  { id: "other", label: "Other" },
];
const AGE_MIN = 18;
const AGE_MAX = 65;

const AdvancedSettingsDropdown: React.FC<AdvancedSettingsDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onNext,
  onAgeChange,
  onGenderChange,
  onAdTextChange,
  onAdHeadlineChange,
  isEditMode = false,
  initialAge,
  initialMaxAge,
  initialGender,
  initialAdText,
  initialAdHeadline,
}) => {
  const isOpen = expandedId === id;
  const [minAge, setMinAge] = useState<number>(18);
  const [maxAge, setMaxAge] = useState<number>(65);
  const [gender, setGender] = useState<string>("all");
  const [adText, setAdText] = useState<string>("");
  const [adHeadline, setAdHeadline] = useState<string>(
    "Listen Now on Spotify!",
  );
  const [minAgeInput, setMinAgeInput] = useState<string>("18");
  const [maxAgeInput, setMaxAgeInput] = useState<string>("65");
  const [isEditingMaxAge, setIsEditingMaxAge] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    setIsLargeScreen(mq.matches);
    const handler = () => setIsLargeScreen(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const formatMaxAge = (age: number) => (age >= 65 ? "65+" : String(age));
  const buildAgeLabel = (min: number, max: number) =>
    min === 18 && max >= 65 ? "All age" : `${min}-${formatMaxAge(max)}`;
  const getThumbPercentage = (value: number) =>
    ((value - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100;

  // Initialize values from props when draft data is available
  useEffect(() => {
    if (
      initialAge !== undefined &&
      initialAge !== null &&
      initialMaxAge !== undefined &&
      initialMaxAge !== null
    ) {
      const clampedMin = Math.min(64, Math.max(AGE_MIN, initialAge));
      const clampedMax = Math.min(
        AGE_MAX,
        Math.max(
          clampedMin + 1,
          initialMaxAge >= AGE_MAX ? AGE_MAX : initialMaxAge,
        ),
      );
      setMinAge(clampedMin);
      setMaxAge(clampedMax);
      setMinAgeInput(String(clampedMin));
      setMaxAgeInput(String(clampedMax));
    }

    if (initialGender !== undefined && initialGender !== null) {
      if (initialGender === 1) {
        setGender("male");
        onGenderChange?.("Male");
      } else if (initialGender === 2) {
        setGender("female");
        onGenderChange?.("Female");
      } else {
        setGender("all");
        onGenderChange?.("All");
      }
    }

    if (initialAdText !== undefined && initialAdText !== null) {
      setAdText(initialAdText);
      onAdTextChange?.(initialAdText);
    }

    if (initialAdHeadline !== undefined && initialAdHeadline !== null) {
      setAdHeadline(initialAdHeadline);
      onAdHeadlineChange?.(initialAdHeadline);
    }
  }, [
    initialAge,
    initialMaxAge,
    initialGender,
    initialAdText,
    initialAdHeadline,
    onGenderChange,
    onAdTextChange,
    onAdHeadlineChange,
  ]);

  useEffect(() => {
    onAgeChange?.(buildAgeLabel(minAge, maxAge));
  }, [minAge, maxAge, onAgeChange]);

  // Sync input state when age values change from slider
  useEffect(() => {
    setMinAgeInput(String(minAge));
    setMaxAgeInput(String(maxAge));
  }, [minAge, maxAge]);

  // Initialize default adHeadline value on mount (only if no initial value)
  useEffect(() => {
    if (initialAdHeadline === undefined || initialAdHeadline === null) {
      onAdHeadlineChange?.("Listen Now on Spotify!");
    }
  }, [onAdHeadlineChange, initialAdHeadline]);

  return (
    <div className="w-full">
      <div
        className="relative rounded-[12px] sm:rounded-[16px] md:rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden w-full min-w-0"
        style={{
          background: "#878DA11A",
          backgroundImage: `url(${ellipseBg})`,
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Header - same left padding as body so title and content align */}
        <div
          className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-90 transition-opacity min-h-[60px] h-[60px] sm:min-h-[72px] sm:h-[72px] md:min-h-[88px] md:h-[88px] px-4 py-4 sm:px-4 sm:py-4 md:px-6 md:py-6"
          onClick={() => onToggle(id)}
        >
          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
            <StepNumberBadge stepNumber={9} isOpen={isOpen} />
            <span
              className="text-[16px] lg:text-[20px] whitespace-nowrap"
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                lineHeight: "130%",
                color: isOpen ? "#58F2A9" : "#FFFFFF",
              }}
            >
              Advanced Settings
            </span>
          </div>

          <img
            src={downArrow}
            alt="Toggle"
            className="pointer-events-none transition-transform flex-shrink-0"
            style={{
              height: "9.5px",
              width: "auto",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              filter: isOpen
                ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                : "none",
            }}
          />
        </div>

        {isOpen && (
          <div className="px-4 pb-4 sm:px-4 sm:pb-4 md:px-6 md:pb-6">
            {/* Align content with header text: same left as "Advanced Settings" (icon 32px + gap 12px = 44px) */}
            <div className="flex flex-col gap-3 md:gap-6 w-full max-w-full min-w-0 md:ml-[41px] md:w-[calc(100%-41px)]">
              {/* Age range */}
              <div className="flex flex-col gap-3 w-full items-start">
                <span className="text-white text-[14px] lg:text-[16px] font-medium [font-family:'Poppins',Helvetica]">
                  Target audience age range
                </span>

                <div
                  className="hidden md:block w-full rounded-[16px] md:rounded-[18px] px-4 md:px-5 py-3 md:py-4"
                  style={{
                    backgroundColor: "rgba(31, 39, 51, 0.95)",
                    opacity: isEditMode ? 0.7 : 1,
                  }}
                >
                  <div className="relative h-[28px] mb-2">
                    <span
                      className="absolute h-[28px] min-w-[56px] px-3 rounded-full bg-[#5D6678] text-white text-xs md:text-sm inline-flex items-center justify-center"
                      style={{
                        left: `${getThumbPercentage(minAge)}%`,
                        transform: "translateX(-50%)",
                      }}
                    >
                      {minAge}
                      <span className="absolute left-1/2 -bottom-[5px] -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent border-t-[#5D6678]" />
                    </span>
                    <span
                      className="absolute h-[28px] min-w-[56px] px-3 rounded-full bg-[#5D6678] text-white text-xs md:text-sm inline-flex items-center justify-center"
                      style={{
                        left: `${getThumbPercentage(maxAge)}%`,
                        transform: "translateX(-50%)",
                      }}
                    >
                      {formatMaxAge(maxAge)}
                      <span className="absolute left-1/2 -bottom-[5px] -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent border-t-[#5D6678]" />
                    </span>
                  </div>

                  <Slider
                    value={[minAge, maxAge]}
                    onChange={(_, value) => {
                      if (isEditMode || !Array.isArray(value)) return;
                      const nextMin = Math.min(64, Math.max(AGE_MIN, value[0]));
                      const nextMax = Math.min(
                        AGE_MAX,
                        Math.max(nextMin + 1, value[1]),
                      );
                      setMinAge(nextMin);
                      setMaxAge(nextMax);
                      setMinAgeInput(String(nextMin));
                      setMaxAgeInput(String(nextMax));
                    }}
                    min={AGE_MIN}
                    max={AGE_MAX}
                    disableSwap
                    disabled={isEditMode}
                    sx={{
                      color: "#58F2A9",
                      height: 10,
                      padding: "8px 0",
                      "& .MuiSlider-rail": {
                        backgroundColor: "#58F2A9",
                        opacity: 0.35,
                        height: 10,
                      },
                      "& .MuiSlider-track": {
                        backgroundColor: "#58F2A9",
                        border: 0,
                        height: 10,
                      },
                      "& .MuiSlider-thumb": {
                        width: 22,
                        height: 22,
                        backgroundColor: "#58F2A9",
                        border: "4px solid #FFFFFF",
                        boxShadow: "none",
                        "&::before": { display: "none" },
                        "&:hover, &.Mui-focusVisible, &.Mui-active": {
                          boxShadow: "none",
                        },
                      },
                    }}
                  />
                </div>

                <div className="flex md:hidden items-center gap-3 w-full justify-center">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={minAgeInput}
                    onChange={(e) => {
                      if (isEditMode) return;
                      setMinAgeInput(e.target.value);
                    }}
                    onBlur={() => {
                      const value = parseInt(minAgeInput);
                      let clampedMin: number;
                      if (isNaN(value) || minAgeInput === "") {
                        clampedMin = AGE_MIN;
                      } else {
                        clampedMin = Math.min(64, Math.max(AGE_MIN, value));
                      }
                      const clampedMax = Math.max(clampedMin + 1, maxAge);
                      setMinAge(clampedMin);
                      setMaxAge(clampedMax);
                      setMinAgeInput(String(clampedMin));
                    }}
                    disabled={isEditMode}
                    min={AGE_MIN}
                    max={AGE_MAX - 1}
                    className="h-[40px] w-[80px] min-w-[80px] px-5 rounded-full bg-[#1B2731] text-white text-lg leading-none inline-flex items-center justify-center text-center focus:outline-none focus:ring-2 focus:ring-[#58F2A9] [-moz-appearance:_textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
                    style={{
                      opacity: isEditMode ? 0.6 : 1,
                      cursor: isEditMode ? "not-allowed" : "text",
                      backgroundColor: isEditMode
                        ? "rgba(27, 39, 49, 0.6)"
                        : "#1B2731",
                    }}
                  />
                  <span className="text-[#878DA1] text-xl leading-none">-</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={
                      maxAge >= 65 && !isEditingMaxAge ? "65+" : maxAgeInput
                    }
                    onFocus={() => {
                      setIsEditingMaxAge(true);
                      if (maxAge >= 65) setMaxAgeInput("65");
                    }}
                    onChange={(e) => {
                      if (isEditMode) return;
                      setMaxAgeInput(e.target.value.replace(/\D/g, ""));
                    }}
                    onBlur={() => {
                      setIsEditingMaxAge(false);
                      const value = parseInt(maxAgeInput);
                      let clampedMax: number;
                      if (isNaN(value) || maxAgeInput === "") {
                        clampedMax = AGE_MAX;
                      } else {
                        clampedMax = Math.min(
                          AGE_MAX,
                          Math.max(minAge + 1, value),
                        );
                      }
                      setMaxAge(clampedMax);
                      setMaxAgeInput(
                        clampedMax >= 65 ? "65" : String(clampedMax),
                      );
                    }}
                    disabled={isEditMode}
                    className="h-[40px] w-[80px] min-w-[80px] px-5 rounded-full bg-[#1B2731] text-white text-lg leading-none inline-flex items-center justify-center text-center focus:outline-none focus:ring-2 focus:ring-[#58F2A9]"
                    style={{
                      opacity: isEditMode ? 0.6 : 1,
                      cursor: isEditMode ? "not-allowed" : "text",
                      backgroundColor: isEditMode
                        ? "rgba(27, 39, 49, 0.6)"
                        : "#1B2731",
                    }}
                  />
                </div>

                <div className="hidden md:flex items-center gap-3 w-full justify-start">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={minAgeInput}
                    onChange={(e) => {
                      if (isEditMode) return;
                      setMinAgeInput(e.target.value);
                    }}
                    onBlur={() => {
                      const value = parseInt(minAgeInput);
                      let clampedMin: number;
                      if (isNaN(value) || minAgeInput === "") {
                        clampedMin = AGE_MIN;
                      } else {
                        clampedMin = Math.min(64, Math.max(AGE_MIN, value));
                      }
                      const clampedMax = Math.max(clampedMin + 1, maxAge);
                      setMinAge(clampedMin);
                      setMaxAge(clampedMax);
                      setMinAgeInput(String(clampedMin));
                      setMaxAgeInput(String(clampedMax));
                    }}
                    disabled={isEditMode}
                    min={AGE_MIN}
                    max={AGE_MAX - 1}
                    className="h-[40px] w-[80px] min-w-[80px] px-5 rounded-full bg-[#1B2731] text-white text-lg leading-none inline-flex items-center justify-center text-center focus:outline-none focus:ring-2 focus:ring-[#58F2A9] [-moz-appearance:_textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
                    style={{
                      opacity: isEditMode ? 0.6 : 1,
                      cursor: isEditMode ? "not-allowed" : "text",
                      backgroundColor: isEditMode
                        ? "rgba(27, 39, 49, 0.6)"
                        : "#1B2731",
                    }}
                  />
                  <span className="text-[#878DA1] text-xl leading-none">-</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={
                      maxAge >= 65 && !isEditingMaxAge ? "65+" : maxAgeInput
                    }
                    onFocus={() => {
                      setIsEditingMaxAge(true);
                      if (maxAge >= 65) setMaxAgeInput("65");
                    }}
                    onChange={(e) => {
                      if (isEditMode) return;
                      setMaxAgeInput(e.target.value.replace(/\D/g, ""));
                    }}
                    onBlur={() => {
                      setIsEditingMaxAge(false);
                      const value = parseInt(maxAgeInput);
                      let clampedMax: number;
                      if (isNaN(value) || maxAgeInput === "") {
                        clampedMax = AGE_MAX;
                      } else {
                        clampedMax = Math.min(
                          AGE_MAX,
                          Math.max(minAge + 1, value),
                        );
                      }
                      setMaxAge(clampedMax);
                      setMaxAgeInput(
                        clampedMax >= 65 ? "65" : String(clampedMax),
                      );
                    }}
                    disabled={isEditMode}
                    className="h-[40px] w-[80px] min-w-[80px] px-5 rounded-full bg-[#1B2731] text-white text-lg leading-none inline-flex items-center justify-center text-center focus:outline-none focus:ring-2 focus:ring-[#58F2A9]"
                    style={{
                      opacity: isEditMode ? 0.6 : 1,
                      cursor: isEditMode ? "not-allowed" : "text",
                      backgroundColor: isEditMode
                        ? "rgba(27, 39, 49, 0.6)"
                        : "#1B2731",
                    }}
                  />
                </div>

                <div className="border-b border-[#1F2733] w-full mt-1" />
              </div>

              {/* Gender */}
              <div className="flex flex-col gap-3 w-full items-start">
                <span className="text-white text-[14px] lg:text-[16px] font-medium [font-family:'Poppins',Helvetica]">
                  Gender
                </span>

                <div className="flex flex-col gap-3 w-full">
                  {GENDER_OPTIONS.map((opt) => {
                    const active = gender === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          if (!isEditMode) {
                            setGender(opt.id);
                            onGenderChange?.(opt.label);
                          }
                        }}
                        disabled={isEditMode}
                        className="flex items-center gap-3 text-white text-sm md:text-[16px] font-normal [font-family:'Poppins',Helvetica] text-left"
                        style={{
                          opacity: isEditMode ? 0.6 : 1,
                          cursor: isEditMode ? "not-allowed" : "pointer",
                        }}
                      >
                        <span
                          className={`inline-flex h-5 w-5 rounded-full flex-shrink-0 ${
                            active
                              ? "border-2 border-[#58F2A9] bg-[#58F2A9]"
                              : "border-2 border-[#878DA199] bg-transparent"
                          }`}
                        />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                <div className="border-b border-[#E5E7EB]/30 w-full" />
              </div>

              {/* Ad Text */}
              <div className="flex flex-col gap-2 w-full">
                <span className="text-white text-[14px] lg:text-[16px] font-medium">
                  Ad Text
                </span>
                <textarea
                  rows={1}
                  value={adText}
                  onChange={(e) => {
                    if (!isEditMode && e.target.value.length <= 300) {
                      setAdText(e.target.value);
                      onAdTextChange?.(e.target.value);
                    }
                  }}
                  maxLength={300}
                  placeholder="Type top headline text..."
                  disabled={isEditMode}
                  className="w-full   px-4 lg:py-[17px] py-[14px] leading-5 text-[#020202] text-[14px] resize-none focus:ring-2 focus:ring-[#58F2A9]  rounded-[12px]"
                  style={{
                    opacity: isEditMode ? 0.6 : 1,
                    backgroundColor: isEditMode ? "#f0f0f0" : "#FFFFFF",
                    cursor: isEditMode ? "not-allowed" : "text",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Divider between Ad Text and Ad Headline */}
              <div className="border-b border-[#E5E7EB]/30 w-full" />

              {/* Ad Headline */}
              <div className="flex flex-col gap-2 w-full">
                <span className="text-white text-[14px] lg:text-[16px]  font-medium">
                  Ad Headline
                </span>
                <textarea
                  rows={1}
                  value={adHeadline}
                  onChange={(e) => {
                    if (!isEditMode && e.target.value.length <= 40) {
                      setAdHeadline(e.target.value);
                      onAdHeadlineChange?.(e.target.value);
                    }
                  }}
                  maxLength={40}
                  placeholder="Type bottom headline text..."
                  disabled={isEditMode}
                  className="w-full   px-4 lg:py-[17px] py-[14px] leading-5 text-[#020202] text-[14px] resize-none focus:ring-2 focus:ring-[#58F2A9]  rounded-[12px]"
                  style={{
                    opacity: isEditMode ? 0.6 : 1,
                    backgroundColor: isEditMode ? "#f0f0f0" : "#FFFFFF",
                    cursor: isEditMode ? "not-allowed" : "text",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Divider above Next button */}
              <div className="border-b border-[#E5E7EB]/30 w-full" />

              {/* Next button - right-aligned, white pill, black text + arrow */}
              <div className="flex w-full justify-end pt-1">
                <NextButton
                  size="sm"
                  className="w-full md:w-auto"
                  onClick={() => onNext?.(id)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedSettingsDropdown;
